import { saveExportFile, waitForQueuedFile, type ExportFile } from './exports'

/**
 * F-140 (TASK-0394, REQ-064-004 / REQ-238-001 / REQ-026-004) — ponte fra Excel e il resto del CRM.
 *
 * Deliberatamente autonomo rispetto a `api()` di `./client.ts`, come `documents.ts`: `to-csv` e' un caricamento
 * `multipart/form-data` (il browser imposta da solo Content-Type e boundary) e `xlsx` restituisce un file.
 */

export interface SpreadsheetCsv {
  csv: string
  sheet: string
  sheets: string[]
  rows: number
}

export interface XlsxRequest {
  title: string
  columns: string[]
  rows: (string | number | boolean | null)[][]
  /** Falso quando le righe contengono piu' blocchi (il filtro automatico li mescolerebbe). */
  autoFilter?: boolean
}

async function csrfHeaders(): Promise<Record<string, string>> {
  const res = await fetch('/api/antiforgery/token', { credentials: 'include' })
  if (!res.ok) return {}
  const data = (await res.json()) as { token: string }
  return { 'X-XSRF-TOKEN': data.token }
}

async function errorMessage(res: Response): Promise<string> {
  const body = await res.json().catch(() => null) as { title?: string; message?: string; error?: string } | null
  return body?.title ?? body?.message ?? body?.error ?? `HTTP ${res.status}`
}

export const spreadsheetsApi = {
  /** `POST /api/spreadsheets/to-csv`: legge un .xlsx e restituisce il CSV da passare agli import di sempre. */
  toCsv: async (file: File, sheet?: string): Promise<SpreadsheetCsv> => {
    const form = new FormData()
    form.append('file', file)
    if (sheet) form.append('sheet', sheet)
    const res = await fetch('/api/spreadsheets/to-csv', {
      method: 'POST',
      credentials: 'include',
      headers: await csrfHeaders(),
      body: form,
    })
    if (!res.ok) throw new Error(await errorMessage(res))
    return res.json() as Promise<SpreadsheetCsv>
  },

  /** `POST /api/spreadsheets/xlsx`: scrive in Excel la tabella che l'utente ha gia' a video. */
  toXlsx: async (request: XlsxRequest, format: 'xlsx' | 'pdf' = 'xlsx'): Promise<ExportFile> => {
    const res = await fetch(`/api/spreadsheets/${format}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...(await csrfHeaders()) },
      body: JSON.stringify(request),
    })
    if (res.status === 202) {
      // R3-19: export grande messo in coda (flag job.export_asincroni): si attende il file del job.
      const queued = await res.json().catch(() => null) as { jobId?: number } | null
      if (queued?.jobId) return waitForQueuedFile(queued.jobId)
    }
    if (!res.ok) throw new Error(await errorMessage(res))
    const disposition = res.headers.get('Content-Disposition') ?? ''
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition)
    return { blob: await res.blob(), filename: match?.[1] ? decodeURIComponent(match[1]) : `report.${format}` }
  },

  /** Converte e fa scaricare. */
  downloadXlsx: async (request: XlsxRequest): Promise<void> => saveExportFile(await spreadsheetsApi.toXlsx(request)),

  /** C-14: la stessa tabella in PDF (`POST /api/spreadsheets/pdf`). */
  downloadPdf: async (request: XlsxRequest): Promise<void> => saveExportFile(await spreadsheetsApi.toXlsx(request, 'pdf')),
}
